import { inject, Injectable } from '@angular/core';
import {
    Auth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    updateProfile,
} from '@angular/fire/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { from, Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { signInAnonymously } from '@angular/fire/auth';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    static helpOpen: boolean = false;

    firebaseAuth = inject(Auth);

    /**
     * Observable für den aktuellen User-Status
     */
    private currentUserSubject = new BehaviorSubject<string | null>(null);
    currentUser$ = this.currentUserSubject.asObservable();

    /**
     * Injects Router for navigation
     * @param router Angular Router
     */
    constructor(private router: Router) {}

    /**
     * Registers a new user with email, username, and password
     * @param email User's email
     * @param username User's display name
     * @param password User's password
     * @returns Observable that completes when registration and profile update are done
     */
    register(
        email: string,
        username: string,
        password: string
    ): Observable<void> {
        const promise = createUserWithEmailAndPassword(
            this.firebaseAuth,
            email,
            password
        ).then((response) =>
            updateProfile(response.user, {
                displayName: username,
            })
        ).then(() => {
            this.currentUserSubject.next(email);
        });
        return from(promise);
    }

    /**
     * Logs in a user with email and password
     * @param email User's email
     * @param password User's password
     * @returns Observable that completes when login is successful or fails
     */
    login(email: string, password: string): Observable<void> {
        const promise = signInWithEmailAndPassword(
            this.firebaseAuth,
            email,
            password
        ).then(
            (res) => {
                localStorage.setItem('token', 'true');
                this.currentUserSubject.next(email);
            },
            (err) => {
                localStorage.setItem('token', 'false');
                throw err;
            }
        );
        return from(promise);
    }

    guestLogin(): Observable<void> {
        const promise = signInAnonymously(this.firebaseAuth)
            .then((userCredential) => {
                console.log('Gast eingeloggt:', userCredential.user.uid);
                localStorage.setItem('token', 'guest');
                this.currentUserSubject.next('guest');
            })
            .catch((error) => {
                console.error('Fehler beim Gastlogin:', error.message);
                throw error;
            });

        return from(promise);
    }
    /**
     * Logs out the current user, clears token, and navigates to login page
     */
    logout(): void {
        this.firebaseAuth.signOut().then(
            () => {
                localStorage.removeItem('token');
                this.currentUserSubject.next(null);
                this.router.navigate(['/login']);
            },
            (err) => {
                alert(err.errorMessage);
            }
        );
    }

    /**
     * Returns the email of the currently logged-in user
     * @returns Email string or null if no user is logged in
     */
    loggedInUser(): string | null {
    return this.currentUserSubject.value;
    }
}
